import { useEffect, useState } from "react";
import { ADSENSE_GLOBAL_STATUS_ATTRIBUTE, getAdsenseGlobalScriptStatus, parseAdsenseGlobalScript, type AdsenseGlobalScriptStatus } from "@/lib/adsense";
import { AD_PROVIDER_OPTIONS, AD_SLOT_CATALOG, normalizeAdSettings, type AdProvider, type AdSettings, type AdSlotConfig, type AdSlotKey } from "@/lib/adSettings";

interface AdSettingsPanelProps {
  adSettings: AdSettings;
  onChange: (next: AdSettings) => void;
}

const PROVIDER_HELPERS: Record<AdProvider, { slotCodeLabel: string; slotCodeHint: string; secondaryLabel: string; secondaryHint: string }> = {
  adsense: {
    slotCodeLabel: "Código do slot",
    slotCodeHint: "Ex.: 1234567890 (data-ad-slot)",
    secondaryLabel: "Código secundário",
    secondaryHint: "Opcional. Use para observações internas ou mapping externo.",
  },
  gam: {
    slotCodeLabel: "Ad unit path",
    slotCodeHint: "Ex.: /1234567/afiliabr/article-top",
    secondaryLabel: "Tamanho / targeting",
    secondaryHint: "Opcional. Use para anotações de line item, key-values ou variações.",
  },
  monetag: {
    slotCodeLabel: "Zone / placement code",
    slotCodeHint: "Informe o identificador da zona ou placement da Monetag.",
    secondaryLabel: "Script complementar",
    secondaryHint: "Opcional. Use para um segundo identificador da rede, se necessário.",
  },
  ezoic: {
    slotCodeLabel: "Placeholder ID",
    slotCodeHint: "Ex.: ezoic-pub-ad-placeholder-101",
    secondaryLabel: "Referência interna",
    secondaryHint: "Opcional. Útil para mapear posições com o painel da Ezoic.",
  },
  mediavine: {
    slotCodeLabel: "Nome do placement",
    slotCodeHint: "Ex.: article-sidebar-1",
    secondaryLabel: "Referência interna",
    secondaryHint: "Opcional. Use para anotações de mapping no painel da Mediavine.",
  },
};

const STATUS_COPY: Record<AdsenseGlobalScriptStatus, { title: string; description: string; className: string; icon: string }> = {
  active: {
    title: "Script global ativo no site",
    description: "O script do Google AdSense está validado e presente no <head> das páginas do site.",
    className: "border-green-200 bg-green-50 text-green-700",
    icon: "ri-checkbox-circle-line",
  },
  removed: {
    title: "Script removido",
    description: "Não há script global do AdSense ativo no site neste momento.",
    className: "border-gray-200 bg-gray-50 text-gray-600",
    icon: "ri-delete-bin-line",
  },
  invalid: {
    title: "Script inválido",
    description: "O script salvo não pôde ser validado e não foi mantido no <head> global do site.",
    className: "border-red-200 bg-red-50 text-red-700",
    icon: "ri-error-warning-line",
  },
};

export default function AdSettingsPanel({ adSettings, onChange }: AdSettingsPanelProps) {
  const updateProvider = <TKey extends keyof AdSettings["providers"]>(provider: TKey, partial: Partial<AdSettings["providers"][TKey]>) => {
    onChange(normalizeAdSettings({
      ...adSettings,
      providers: {
        ...adSettings.providers,
        [provider]: {
          ...adSettings.providers[provider],
          ...partial,
        },
      },
    }));
  };

  const updateSlot = (slotKey: AdSlotKey, partial: Partial<AdSlotConfig>) => {
    onChange(normalizeAdSettings({
      ...adSettings,
      slots: {
        ...adSettings.slots,
        [slotKey]: {
          ...adSettings.slots[slotKey],
          ...partial,
        },
      },
    }));
  };

  const adsenseScriptValidation = parseAdsenseGlobalScript(adSettings.providers.adsense.globalScriptCode);
  const [globalStatus, setGlobalStatus] = useState<AdsenseGlobalScriptStatus>(() => getAdsenseGlobalScriptStatus());

  useEffect(() => {
    const root = document.documentElement;
    const syncStatus = () => {
      setGlobalStatus(getAdsenseGlobalScriptStatus());
    };

    syncStatus();

    const observer = new MutationObserver(syncStatus);
    observer.observe(root, {
      attributes: true,
      attributeFilter: [ADSENSE_GLOBAL_STATUS_ATTRIBUTE],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const statusCopy = STATUS_COPY[globalStatus];

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-orange-50">
            <i className="ri-money-dollar-circle-line text-orange-500 text-base"></i>
          </div>
          <h2 className="text-base font-bold text-gray-800">Monetização & Slots Nomeados</h2>
        </div>
        <p className="text-sm text-gray-500">
          Configure uma vez os slots por posição e troque a rede quando quiser, sem alterar o layout dos artigos novamente.
        </p>
      </div>

      <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-800 space-y-2">
        <div className="flex items-center gap-2 font-semibold">
          <i className="ri-information-line"></i>
          Lazy load e scroll trigger continuam ativos no front-end
        </div>
        <p>
          Os espaços do artigo permanecem desacoplados do provedor. Você só precisa informar o código da posição e, se necessário, o script global da rede.
        </p>
      </div>

      <section className="space-y-4">
        <div>
          <h3 className="text-sm font-bold text-gray-800">Redes disponíveis</h3>
          <p className="text-xs text-gray-500 mt-1">Ative apenas as integrações que realmente estiver usando.</p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <ProviderCard
            title="Google AdSense"
            icon="ri-google-line"
            enabled={adSettings.providers.adsense.enabled}
            onToggle={(enabled) => updateProvider("adsense", { enabled })}
          >
            <div className={`rounded-xl border px-4 py-3 ${statusCopy.className}`}>
              <div className="flex items-start gap-3">
                <i className={`${statusCopy.icon} text-base mt-0.5`}></i>
                <div>
                  <p className="text-sm font-bold">{statusCopy.title}</p>
                  <p className="text-xs mt-1">{statusCopy.description}</p>
                </div>
              </div>
            </div>

            <Field label="Publisher Client ID" hint="Ex.: ca-pub-1234567890123456">
              <input
                type="text"
                value={adSettings.providers.adsense.clientId}
                onChange={(e) => updateProvider("adsense", { clientId: e.target.value })}
                placeholder="ca-pub-1234567890123456"
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors"
              />
            </Field>
            <Field label="Script global" hint="Cole o snippet oficial completo do Google AdSense. Ao salvar, ele será validado e inserido globalmente no <head> do site.">
              <textarea
                value={adSettings.providers.adsense.globalScriptCode}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  const parsedScript = parseAdsenseGlobalScript(nextValue);

                  updateProvider("adsense", {
                    globalScriptCode: nextValue,
                    clientId: parsedScript?.clientId || adSettings.providers.adsense.clientId,
                    scriptUrl: parsedScript?.src || adSettings.providers.adsense.scriptUrl,
                  });
                }}
                rows={5}
                placeholder={'<script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1234567890123456" crossorigin="anonymous"></script>'}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm font-mono focus:outline-none focus:border-orange-400 transition-colors resize-y"
              />
            </Field>
            <div className={`rounded-xl border px-4 py-3 text-xs ${
              !adSettings.providers.adsense.globalScriptCode.trim()
                ? "border-gray-200 bg-gray-50 text-gray-500"
                : adsenseScriptValidation
                  ? "border-green-200 bg-green-50 text-green-700"
                  : "border-red-200 bg-red-50 text-red-700"
            }`}>
              {!adSettings.providers.adsense.globalScriptCode.trim()
                ? "Nenhum script global configurado. Ao salvar vazio, o sistema remove a inclusão global do AdSense do head."
                : adsenseScriptValidation
                  ? "Script válido. Ao salvar, ele será mantido validado no head de todas as páginas do site."
                  : "Script inválido. Cole o snippet oficial do Google AdSense com https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js e o client ca-pub."}
            </div>
          </ProviderCard>

          <ProviderCard
            title="Google Ad Manager"
            icon="ri-layout-grid-line"
            enabled={adSettings.providers.gam.enabled}
            onToggle={(enabled) => updateProvider("gam", { enabled })}
          >
            <Field label="Network code" hint="Ex.: 1234567">
              <input
                type="text"
                value={adSettings.providers.gam.networkCode}
                onChange={(e) => updateProvider("gam", { networkCode: e.target.value })}
                placeholder="1234567"
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors"
              />
            </Field>
            <Field label="Script global" hint="Script GPT oficial do Google.">
              <input
                type="url"
                value={adSettings.providers.gam.scriptUrl}
                onChange={(e) => updateProvider("gam", { scriptUrl: e.target.value })}
                placeholder="https://securepubads.g.doubleclick.net/tag/js/gpt.js"
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors"
              />
            </Field>
          </ProviderCard>

          <ProviderCard
            title="Monetag"
            icon="ri-flashlight-line"
            enabled={adSettings.providers.monetag.enabled}
            onToggle={(enabled) => updateProvider("monetag", { enabled })}
          >
            <Field label="Script global" hint="Use a URL oficial entregue pela Monetag para o formato desejado.">
              <input
                type="url"
                value={adSettings.providers.monetag.scriptUrl}
                onChange={(e) => updateProvider("monetag", { scriptUrl: e.target.value })}
                placeholder="https://...monetag.com/...js"
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors"
              />
            </Field>
          </ProviderCard>

          <ProviderCard
            title="Ezoic / Mediavine"
            icon="ri-stack-line"
            enabled={adSettings.providers.ezoic.enabled || adSettings.providers.mediavine.enabled}
            onToggle={(enabled) => onChange(normalizeAdSettings({
              ...adSettings,
              providers: {
                ...adSettings.providers,
                ezoic: {
                  ...adSettings.providers.ezoic,
                  enabled,
                },
                mediavine: {
                  ...adSettings.providers.mediavine,
                  enabled,
                },
              },
            }))}
          >

            <div className="grid grid-cols-1 gap-4">
              <div className="rounded-xl border border-gray-100 p-4 bg-gray-50/70 space-y-3">
                <label className="flex items-center justify-between gap-3 text-sm font-semibold text-gray-700">
                  <span>Ezoic ativo</span>
                  <input
                    type="checkbox"
                    checked={adSettings.providers.ezoic.enabled}
                    onChange={(e) => updateProvider("ezoic", { enabled: e.target.checked })}
                    className="w-4 h-4 accent-orange-500"
                  />
                </label>
                <Field label="Script global da Ezoic" hint="Opcional se já estiver sendo injetado pelo tema/base do site.">
                  <input
                    type="url"
                    value={adSettings.providers.ezoic.scriptUrl}
                    onChange={(e) => updateProvider("ezoic", { scriptUrl: e.target.value })}
                    placeholder="https://go.ezoic.net/..."
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                  />
                </Field>
              </div>

              <div className="rounded-xl border border-gray-100 p-4 bg-gray-50/70 space-y-3">
                <label className="flex items-center justify-between gap-3 text-sm font-semibold text-gray-700">
                  <span>Mediavine ativa</span>
                  <input
                    type="checkbox"
                    checked={adSettings.providers.mediavine.enabled}
                    onChange={(e) => updateProvider("mediavine", { enabled: e.target.checked })}
                    className="w-4 h-4 accent-orange-500"
                  />
                </label>
                <Field label="Site ID" hint="Opcional. Útil como referência operacional.">
                  <input
                    type="text"
                    value={adSettings.providers.mediavine.siteId}
                    onChange={(e) => updateProvider("mediavine", { siteId: e.target.value })}
                    placeholder="mv-123456"
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                  />
                </Field>
                <Field label="Script global da Mediavine" hint="Opcional se já estiver sendo carregado globalmente.">
                  <input
                    type="url"
                    value={adSettings.providers.mediavine.scriptUrl}
                    onChange={(e) => updateProvider("mediavine", { scriptUrl: e.target.value })}
                    placeholder="https://scripts.mediavine.com/..."
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                  />
                </Field>
              </div>
            </div>
          </ProviderCard>
        </div>
      </section>

      <section className="space-y-4">
        <div>
          <h3 className="text-sm font-bold text-gray-800">Slots por posição</h3>
          <p className="text-xs text-gray-500 mt-1">Cada slot mantém o layout atual e só troca a integração e o código usado.</p>
        </div>

        <div className="space-y-4">
          {(Object.keys(AD_SLOT_CATALOG) as AdSlotKey[]).map((slotKey) => {
            const slot = adSettings.slots[slotKey];
            const helper = PROVIDER_HELPERS[slot.provider];

            return (
              <div key={slotKey} className="rounded-2xl border border-gray-100 bg-white p-5">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-sm font-bold text-gray-900">{slot.name}</h4>
                      <span className="inline-flex items-center rounded-full bg-orange-50 px-2.5 py-1 text-[11px] font-semibold text-orange-700">
                        {slot.formatLabel}
                      </span>
                      {!slot.enabled && (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-500">
                          Desativado
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{slot.description}</p>
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={slot.enabled}
                      onChange={(e) => updateSlot(slotKey, { enabled: e.target.checked })}
                      className="w-4 h-4 accent-orange-500"
                    />
                    Slot ativo
                  </label>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <Field label="Provedor" hint="Selecione a rede que deve preencher esta posição.">
                    <select
                      value={slot.provider}
                      onChange={(e) => updateSlot(slotKey, { provider: e.target.value as AdProvider })}
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors bg-white"
                    >
                      {AD_PROVIDER_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Container ID" hint="Identificador estável do DOM para esta posição.">
                    <input
                      type="text"
                      value={slot.containerId}
                      onChange={(e) => updateSlot(slotKey, { containerId: e.target.value })}
                      placeholder={slot.key}
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                    />
                  </Field>

                  <Field label={helper.slotCodeLabel} hint={helper.slotCodeHint}>
                    <input
                      type="text"
                      value={slot.slotCode}
                      onChange={(e) => updateSlot(slotKey, { slotCode: e.target.value })}
                      placeholder="Informe o código deste slot"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                    />
                  </Field>

                  <Field label={helper.secondaryLabel} hint={helper.secondaryHint}>
                    <input
                      type="text"
                      value={slot.secondaryCode}
                      onChange={(e) => updateSlot(slotKey, { secondaryCode: e.target.value })}
                      placeholder="Opcional"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                    />
                  </Field>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function ProviderCard({
  title,
  icon,
  enabled,
  onToggle,
  children,
}: {
  title: string;
  icon: string;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-50 text-orange-500">
            <i className={`${icon} text-lg`}></i>
          </div>
          <div>
            <h4 className="text-sm font-bold text-gray-900">{title}</h4>
            <p className="text-xs text-gray-500 mt-0.5">Configure a integração base desta rede.</p>
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onToggle(e.target.checked)}
            className="w-4 h-4 accent-orange-500"
          />
          Ativo
        </label>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}